// Vorkasse-Sperre: Aufträge von Vorkasse-Kunden werden markiert und gelten
// erst als freigegeben, wenn ein Zahlungsdatum eingetragen wurde. Zeigt sich
// hier als Banner (Detailseite) + als 🔒-Badge in den Übersichten
// (ModuleUebersicht.tsx / Uebersicht.tsx).
import { useState } from 'react';
import { api } from '../lib/api';
import { moduleApi } from '../lib/moduleApi';

type Modul = 'drueckteile' | 'laufraeder' | 'baugruppen' | 'montage' | 'laser_kant';

async function updateVorkasse(modul: Modul, id: number, patch: { vorkasse?: number; vorkasse_bezahlt_am?: string | null }) {
  if (modul === 'drueckteile') {
    await api.drueckteile.update(id, patch as never);
  } else {
    await moduleApi(modul).update(id, patch as never);
  }
}

export function VorkasseBanner({ modul, recordId, vorkasse, bezahltAm, onChanged }: {
  modul: Modul;
  recordId: number;
  vorkasse: number;
  bezahltAm: string | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10));

  const alsVorkasseMarkieren = async () => {
    setBusy(true);
    try { await updateVorkasse(modul, recordId, { vorkasse: 1 }); onChanged(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };
  const vorkasseAufheben = async () => {
    if (!confirm('Vorkasse-Kennzeichnung entfernen?')) return;
    setBusy(true);
    try { await updateVorkasse(modul, recordId, { vorkasse: 0, vorkasse_bezahlt_am: null }); onChanged(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };
  const zahlungEingetragen = async () => {
    if (!datum) return;
    setBusy(true);
    try { await updateVorkasse(modul, recordId, { vorkasse_bezahlt_am: datum }); onChanged(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };
  const zahlungZuruecksetzen = async () => {
    if (!confirm('Zahlungseingang zurücksetzen — Auftrag wieder sperren?')) return;
    setBusy(true);
    try { await updateVorkasse(modul, recordId, { vorkasse_bezahlt_am: null }); onChanged(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };

  if (!vorkasse) {
    return (
      <div className="mb-4">
        <button onClick={() => void alsVorkasseMarkieren()} disabled={busy}
                className="text-xs text-slate-400 hover:text-slate-600 underline disabled:opacity-50">
          🔒 Als Vorkasse-Auftrag markieren
        </button>
      </div>
    );
  }

  if (!bezahltAm) {
    return (
      <div className="mb-4 bg-slate-100 border border-slate-300 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold text-slate-700 flex items-center gap-2">🔒 Vorkasse ausstehend</div>
          <div className="text-sm text-slate-500">Auftrag ist gesperrt, bis die Zahlung eingetragen wird.</div>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" className="input" value={datum} onChange={e => setDatum(e.target.value)} />
          <button onClick={() => void zahlungEingetragen()} disabled={busy || !datum}
                  className="bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50 whitespace-nowrap">
            ✓ Zahlung eingegangen
          </button>
          <button onClick={() => void vorkasseAufheben()} disabled={busy}
                  className="text-xs text-slate-400 hover:text-red-600 underline whitespace-nowrap">
            Vorkasse aufheben
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2">
      <div className="text-sm text-emerald-800">
        ✓ Vorkasse bezahlt am <strong>{new Date(bezahltAm).toLocaleDateString('de-DE')}</strong> — Auftrag freigegeben
      </div>
      <button onClick={() => void zahlungZuruecksetzen()} disabled={busy}
              className="text-xs text-emerald-700 hover:text-red-600 underline whitespace-nowrap">
        zurücksetzen
      </button>
    </div>
  );
}

export function VorkasseLockBadge({ vorkasse, bezahltAm }: { vorkasse: number; bezahltAm: string | null }) {
  if (!vorkasse || bezahltAm) return null;
  return (
    <span className="ml-2 text-xs font-semibold text-slate-500" title="Vorkasse ausstehend — Auftrag gesperrt">
      🔒 Vorkasse
    </span>
  );
}

export function isVorkasseGesperrt(vorkasse: number, bezahltAm: string | null): boolean {
  return !!vorkasse && !bezahltAm;
}
